import { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { exigirTransicao, referenciaCotacao, statusPorRespostas } from '@profitlens/dominio';
import { db } from '../config/firebase';
import type {
  Cotacao,
  ItemCotacao,
  RespostaCotacao,
  StatusCotacao,
  StatusHistCotacao,
} from '../tipos';

export type RascunhoCotacao = Omit<
  Cotacao,
  'id' | 'referencia' | 'status' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

export function cotacaoVazia(responsavelId: string): RascunhoCotacao {
  return {
    fornecedorId: '',
    responsavelId,
    canal: null,
    enviadaEm: null,
    respondidaEm: null,
    validade: null,
    observacoes: null,
  };
}

export const hojeISO = () => new Date().toISOString().slice(0, 10);

// ---------- leitura ----------

export function useCotacoes() {
  const [cotacoes, setCotacoes] = useState<Cotacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'cotacoes'), orderBy('referencia', 'desc')),
        (s) => {
          setCotacoes(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Cotacao));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { cotacoes, carregando, erro };
}

export function useCotacao(id: string | null) {
  const [cotacao, setCotacao] = useState<Cotacao | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!id) {
      setCotacao(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    return onSnapshot(doc(db, 'cotacoes', id), (d) => {
      setCotacao(d.exists() ? ({ id: d.id, ...d.data() } as Cotacao) : null);
      setCarregando(false);
    });
  }, [id]);

  return { cotacao, carregando };
}

export function useItens(cotacaoId: string | null) {
  const [itens, setItens] = useState<ItemCotacao[]>([]);

  useEffect(() => {
    if (!cotacaoId) {
      setItens([]);
      return;
    }
    return onSnapshot(collection(db, 'cotacoes', cotacaoId, 'itens'), (s) =>
      setItens(s.docs.map((d) => ({ id: d.id, ...d.data() }) as ItemCotacao)),
    );
  }, [cotacaoId]);

  return itens;
}

export function useRespostas(cotacaoId: string | null) {
  const [respostas, setRespostas] = useState<RespostaCotacao[]>([]);

  useEffect(() => {
    if (!cotacaoId) {
      setRespostas([]);
      return;
    }
    return onSnapshot(collection(db, 'cotacoes', cotacaoId, 'respostas'), (s) =>
      setRespostas(s.docs.map((d) => ({ id: d.id, ...d.data() }) as RespostaCotacao)),
    );
  }, [cotacaoId]);

  return respostas;
}

export function useStatusHist(cotacaoId: string | null) {
  const [historico, setHistorico] = useState<StatusHistCotacao[]>([]);

  useEffect(() => {
    if (!cotacaoId) {
      setHistorico([]);
      return;
    }
    return onSnapshot(
      query(collection(db, 'cotacoes', cotacaoId, 'statusHist'), orderBy('em', 'desc')),
      (s) => setHistorico(s.docs.map((d) => ({ id: d.id, ...d.data() }) as StatusHistCotacao)),
    );
  }, [cotacaoId]);

  return historico;
}

export interface RespostaComCotacao extends RespostaCotacao {
  /**
   * De qual cotacao esta resposta veio.
   *
   * Nao e campo gravado: sai do caminho do documento. Precisa existir porque o
   * id da resposta e o id do ITEM, unico so dentro da propria cotacao — quatro
   * fornecedores respondendo o mesmo produto devolvem quatro documentos com o
   * MESMO id. Sem isto, a tela colapsa linhas diferentes numa so.
   */
  cotacaoId: string;
}

/**
 * Todas as respostas para um produto, venham da cotacao que vierem.
 *
 * E a consulta que sustenta o comparativo: "quem cotou este produto e por
 * quanto". Ela atravessa as cotacoes, por isso e uma consulta de GRUPO de
 * colecao — e por isso o firestore.rules tem uma regra propria para ela, e o
 * firestore.indexes.json declara o indice de escopo de grupo.
 */
export function useRespostasDoProduto(produtoId: string | null) {
  const [respostas, setRespostas] = useState<RespostaComCotacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!produtoId) {
      setRespostas([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    return onSnapshot(
      query(collectionGroup(db, 'respostas'), where('produtoId', '==', produtoId)),
      (s) => {
        setRespostas(
          s.docs.map(
            (d) =>
              ({
                id: d.id,
                cotacaoId: d.ref.parent.parent?.id ?? '',
                ...d.data(),
              }) as RespostaComCotacao,
          ),
        );
        setCarregando(false);
        setErro(null);
      },
      (e) => {
        setErro(e.message);
        setCarregando(false);
      },
    );
  }, [produtoId]);

  return { respostas, carregando, erro };
}

// ---------- escrita ----------

/**
 * Referencia sequencial legivel (COT-2026-0042).
 *
 * O contador vive num documento so, incrementado em transacao: duas pessoas
 * criando cotacao ao mesmo tempo nao recebem o mesmo numero.
 */
async function proximaReferencia(): Promise<string> {
  const ano = new Date().getFullYear();
  const ref = doc(db, 'contadores', `cotacoes-${ano}`);

  return await runTransaction(db, async (tx) => {
    const atual = await tx.get(ref);
    const sequencial = (atual.exists() ? (atual.data().ultimo as number) : 0) + 1;
    tx.set(ref, { ultimo: sequencial });
    return referenciaCotacao(ano, sequencial);
  });
}

export async function criarCotacao(dados: RascunhoCotacao, usuarioId: string): Promise<string> {
  const referencia = await proximaReferencia();
  const ref = doc(collection(db, 'cotacoes'));

  await setDoc(ref, {
    ...dados,
    referencia,
    status: 'RASCUNHO',
    criadoEm: serverTimestamp(),
    criadoPor: usuarioId,
    atualizadoEm: null,
    atualizadoPor: null,
  });

  await registrarStatus(ref.id, null, 'RASCUNHO', usuarioId, null);
  return ref.id;
}

export async function salvarCotacao(
  id: string,
  dados: Partial<RascunhoCotacao>,
  usuarioId: string,
): Promise<void> {
  await updateDoc(doc(db, 'cotacoes', id), {
    ...dados,
    atualizadoEm: serverTimestamp(),
    atualizadoPor: usuarioId,
  });
}

async function registrarStatus(
  cotacaoId: string,
  de: StatusCotacao | null,
  para: StatusCotacao,
  usuarioId: string,
  observacao: string | null,
): Promise<void> {
  await setDoc(doc(collection(db, 'cotacoes', cotacaoId, 'statusHist')), {
    de,
    para,
    em: serverTimestamp(),
    porUsuarioId: usuarioId,
    observacao,
  });
}

/**
 * Muda o status obedecendo a maquina de estados (RN-040).
 *
 * exigirTransicao lanca em transicao invalida — de proposito. Corrigir em
 * silencio deixaria o historico mentindo sobre o que aconteceu: uma cotacao
 * que saltou de rascunho para respondida nunca foi enviada a ninguem.
 */
export async function transitarStatus(
  cotacaoId: string,
  de: StatusCotacao,
  para: StatusCotacao,
  usuarioId: string,
  observacao: string | null = null,
): Promise<void> {
  exigirTransicao(de, para);

  const marcaDeTempo: Record<string, unknown> = {};
  if (para === 'ENVIADA') marcaDeTempo.enviadaEm = serverTimestamp();
  if (para === 'RESPONDIDA' || para === 'PARCIALMENTE_RESPONDIDA') {
    marcaDeTempo.respondidaEm = serverTimestamp();
  }

  await updateDoc(doc(db, 'cotacoes', cotacaoId), {
    status: para,
    ...marcaDeTempo,
    atualizadoEm: serverTimestamp(),
    atualizadoPor: usuarioId,
  });
  await registrarStatus(cotacaoId, de, para, usuarioId, observacao);
}

export async function adicionarItem(
  cotacaoId: string,
  produtoId: string,
  quantidadeDesejadaMilli: number,
): Promise<void> {
  await setDoc(doc(collection(db, 'cotacoes', cotacaoId, 'itens')), {
    produtoId,
    quantidadeDesejadaMilli,
    observacoes: null,
  });
}

export async function removerItem(cotacaoId: string, itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'cotacoes', cotacaoId, 'itens', itemId));
  await deleteDoc(doc(db, 'cotacoes', cotacaoId, 'respostas', itemId));
}

/** O que a tela preenche. Os campos de procedencia da IA nao passam por aqui. */
export interface DadosResposta {
  precoUnitarioCents: number | null;
  quantidadeMinimaMilli: number | null;
  quantidadeDisponivelMilli: number | null;
  freteCents: number | null;
  impostosCents: number | null;
  prazoEntregaDias: number | null;
  condicaoPagamento: string | null;
  validade: string | null;
  dataResposta: string | null;
  requerRevisao: boolean;
}

/**
 * Grava a resposta do fornecedor e ajusta o status conforme as respostas
 * chegam (RN-043).
 *
 * Toda gravacao daqui e de origem MANUAL. Resposta extraida por IA entra
 * apenas por Cloud Function: ela precisa carregar texto original e confianca
 * juntos (RN-050), e o firestore.rules recusa esse caminho pelo navegador.
 *
 * O id da resposta E o id do item: um item tem no maximo uma resposta por
 * cotacao, e amarrar os dois pelo id torna impossivel gravar duas.
 */
export async function salvarResposta(
  cotacaoId: string,
  item: { id: string; produtoId: string },
  fornecedorId: string,
  dados: DadosResposta,
  usuarioId: string,
  contexto: { totalItens: number; statusAtual: StatusCotacao },
): Promise<void> {
  await setDoc(doc(db, 'cotacoes', cotacaoId, 'respostas', item.id), {
    ...dados,
    itemId: item.id,
    produtoId: item.produtoId,
    fornecedorId,
    origem: 'MANUAL',
    textoOriginal: null,
    confianca: null,
    revisadoPor: null,
    revisadoEm: null,
  });

  const respondidos = await contarRespondidos(cotacaoId);
  const alvo = statusPorRespostas(contexto.totalItens, respondidos, dados.requerRevisao);
  if (alvo && alvo !== contexto.statusAtual) {
    try {
      await transitarStatus(cotacaoId, contexto.statusAtual, alvo, usuarioId, 'resposta registrada');
    } catch {
      // A transicao nao sai deste status (um rascunho, por exemplo). A resposta
      // fica gravada; o status nao se mexe sozinho. Quem move e a pessoa.
    }
  }
}

async function contarRespondidos(cotacaoId: string): Promise<number> {
  const s = await getDocs(collection(db, 'cotacoes', cotacaoId, 'respostas'));
  return s.docs.filter((d) => d.data().precoUnitarioCents !== null).length;
}

export async function removerResposta(cotacaoId: string, respostaId: string): Promise<void> {
  await deleteDoc(doc(db, 'cotacoes', cotacaoId, 'respostas', respostaId));
}
